import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import emailVerificationIcon from "../../../assets/images/emailVerificationicon.svg";
import { getSession } from "../../../Slices/sessionSlice";
import { newAlert } from "../../../Slices/alertSlice";
import Axios from "../../../axios";
import Loading from "../../../components/Loading/Loading";
import "./EmailVerificationPending.css";


const EmailVerificationPending = ({ email, enableButtonAfterSeconds = 120 }) => {
  // State and hooks setup
  const [secondsLeft, setSecondsLeft] = useState(enableButtonAfterSeconds);
  const { sessionUser, isLoading, pendingUser } = useSelector(
    (state) => state.sessionSlice
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get email from props, URL params, or stored pending user
  const displayEmail =
    email ||
    searchParams.get("email") ||
    pendingUser?.email ||
    "";

  // Effect hook for checking session and redirecting logged in users
  // Always call getSession to check if user is verified, but it may fail for pending users
  useEffect(() => {
    dispatch(getSession());
  }, [dispatch]);

  // Effect hook for redirecting logged in users
  useEffect(() => {
    // If session is loaded and user is logged in (has roles), redirect to home
    if (!isLoading && sessionUser && sessionUser.roles && sessionUser.roles.length > 0) {
      navigate("/");
    }
  }, [sessionUser, isLoading, navigate]);

  // Effect hook for countdown timer
  useEffect(() => {
    let timer;
    if (secondsLeft > 0) {
      timer = setInterval(() => {
        setSecondsLeft((prevSecondsLeft) => prevSecondsLeft - 1);
      }, 2000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [secondsLeft]);

  // Handler for resend email button
  const handleResendClick = async () => {
    setSecondsLeft(enableButtonAfterSeconds);
    
    // Get email to resend verification for
    const emailToResend = displayEmail;
    
    if (!emailToResend) {
      console.error("No email found to resend verification");
      return;
    }

    try {
      const response = await Axios.post("/api/session/resend-verification-email");

      // Check if email is already verified
      if (response.data && response.data.isEmailVerified) {
        dispatch(newAlert({ 
          type: "success", 
          message: "Your email is already verified" 
        }));
        // Redirect to sign-in page (landing page with login modal) after a short delay
        setTimeout(() => {
          navigate("/");
        }, 500);
      } else {
        // Show success notification for resend
        dispatch(newAlert({ type: "success", message: "Verification email sent" }));
      }
    } catch (error) {
      console.error("Error resending verification email:", error);
      const errorMessage = error.response?.data?.error?.message 
        || error.response?.data?.message 
        || error.message 
        || "Failed to resend verification email";
      dispatch(newAlert({ type: "danger", message: errorMessage }));
    }
  };

  // Button text calculation
  const buttonText =
    secondsLeft > 0 ? `Resend Email (${secondsLeft}s)` : "Resend Email";

  // Show loading while checking session (but only if we don't have pending user info)
  // If we have pendingUser or email, we can show the page immediately
  if (isLoading && !pendingUser && !searchParams.get("email") && !email) {
    return <Loading />;
  }

  // Render method
  return (
    <div className="email-verification-container">
      <div className="email-verification-card">
        {/* Email verification icon */}
        <div className="email-verification-icon-wrapper">
          <img
            src={emailVerificationIcon}
            alt="email verification icon"
            className="email-verification-icon"
          />
        </div>

        {/* Header and instructions */}
        <h2 className="email-verification-title">
          Verify your email
        </h2>
        <p className="email-verification-text">
          Welcome to{" "}
          <span className="email-verification-product-name">Tracker!</span>
        </p>
        <p className="email-verification-text">
          A verification link has been sent to your email
        </p>

        {/* Email display */}
        {displayEmail && (
          <div className="email-verification-email-display">
            {displayEmail}
          </div>
        )}

        {/* Instructions */}
        <p className="email-verification-text">
          Please verify your email to continue. If you don't see the email,
          please check your spam folder.
        </p>
        <p className="email-verification-text email-verification-text-spacing">
          Did not receive email?
        </p>

        {/* Resend button */}
        <div className="email-verification-button-wrapper">
          <button
            className="email-verification-resend-button"
            disabled={secondsLeft > 0}
            onClick={handleResendClick}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;

